import { useCallback, useEffect, useRef, useState } from "react";

/**
 * IR-1412 r2: an open OPS tab never notices a new deploy — index.html is
 * no-cache but the SPA bundle keeps running until a manual refresh, so users
 * report bugs that are already fixed on prod (happened twice on this ticket).
 * Watch the served index.html `etag` (prod nginx sends etag + no-store on it,
 * verified live 2026-08-23) and surface new deploys to the open tab.
 */

const CHECK_INTERVAL_MS = 2 * 60_000;

export interface VersionWatcherState {
  /** True when a new deploy was detected while the tab is visible and the
   *  user hasn't dismissed that exact version. */
  updateAvailable: boolean;
  /** Hard-refresh to pick up the new bundle. */
  refresh: () => void;
  /** Hide the banner for THIS version; a NEWER etag may re-show it. */
  dismiss: () => void;
}

export function useVersionWatcher(): VersionWatcherState {
  // First etag seen is the baseline (= the version this tab is running).
  const baselineRef = useRef<string | null>(null);
  // Etag the user dismissed the banner for — never nag on the same version.
  // Dismissal also suppresses the hidden-tab auto-reload for that version:
  // "not now" shouldn't turn into a reload the moment they switch tabs.
  const dismissedRef = useRef<string | null>(null);
  const [pendingEtag, setPendingEtag] = useState<string | null>(null);

  const check = useCallback(async () => {
    let etag: string | null = null;
    try {
      const res = await fetch("/", { method: "HEAD", cache: "no-store" });
      etag = res.headers.get("etag");
    } catch {
      // Transient network error — stay quiet, next scheduled check retries.
      return;
    }
    // No etag header (e.g. a proxy stripped it): no check can conclude, so
    // the watcher goes quiet rather than false-positive.
    if (!etag) return;
    if (baselineRef.current === null) {
      baselineRef.current = etag;
      return;
    }
    if (etag === baselineRef.current || etag === dismissedRef.current) return;
    if (document.visibilityState === "hidden") {
      // Nothing on screen to smooth — reload now so the user returns to
      // fresh code instead of a stale bundle.
      window.location.reload();
      return;
    }
    setPendingEtag(etag);
  }, []);

  useEffect(() => {
    void check();
    // `focus` (not visibilitychange): catches both tab switches AND window
    // re-focus after the user was in another app during a deploy.
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    const timer = setInterval(() => void check(), CHECK_INTERVAL_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(timer);
    };
  }, [check]);

  return {
    updateAvailable: pendingEtag !== null,
    refresh: () => window.location.reload(),
    dismiss: () => {
      dismissedRef.current = pendingEtag;
      setPendingEtag(null);
    },
  };
}
